'use client';

import { FunctionComponent, ReactNode, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Project } from '@/app/api';
import { PropsWithClassname } from '@/app/types';
import {
  getBaseUrlForSharing,
  getFacebookShareLink,
  getShareLink,
  getWhatsappShareLink,
  getXShareLink,
} from '@/app/utils/social';

import Button, { ButtonHTMLTag, ButtonProps, ButtonSize, ButtonStyle } from '@/app/components/Button/Button';
import ChevronIcon from '@/app/components/ChevronIcon/ChevronIcon';
import CopyInput from '@/app/components/CopyInput/CopyInput';
import FacebookIcon from '@/app/components/FacebookIcon/FacebookIcon';
import InstagramIcon from '@/app/components/InstagramIcon/InstagramIcon';
import { PrintIcon } from '@/app/components/PrintIcon/PrintIcon';
import ShareIcon from '@/app/components/ShareIcon/ShareIcon';
import WhatsappIcon from '@/app/components/WhatsappIcon/WhatsappIcon';
import XIcon from '@/app/components/XIcon/XIcon';
import useAnalytics from '@/app/hooks/useAnalytics';
import { ShareMethod } from '@/app/hooks/useAnalytics/events/project-detail/shared-event';
import { useCurrentUser } from '@/app/hooks/useCurrentUser';

interface ShareStepProps extends PropsWithClassname {
  project: Project;
  handleInstagramShare: () => void;
  handlePrintPoster: () => void;
}

const ShareButton = ({
  title,
  icon,
  style,
  tagSpecificProps,
  size = ButtonSize.LARGE,
}: {
  title: string;
  icon: ReactNode;
  style: ButtonStyle;
  tagSpecificProps: ButtonProps['tagSpecificProps'];
  size?: ButtonSize;
}) => {
  return (
    <Button
      className="h-[48px] flex px-0 py-0 items-center justify-center"
      size={size}
      style={style}
      tagSpecificProps={tagSpecificProps}
    >
      <div className="flex items-center justify-center">
        <span className="mr-[16px]">{icon}</span>
        <span>{title}</span>
      </div>
    </Button>
  );
};

export const ShareButtons: FunctionComponent<ShareStepProps> = ({
  project,
  handleInstagramShare,
  handlePrintPoster,
}) => {
  const { currentUser } = useCurrentUser();
  const { t } = useTranslation();
  const typeofNavigator = typeof navigator;
  const isNavigatorDefined = typeofNavigator !== 'undefined';
  const isNativeShareDefined = window?.navigator?.share !== undefined;
  const [shouldDisplayMobileOtherOptions, setShouldDisplayMobileOtherOptions] = useState(false);

  const {
    projectDetail: { sharedEvent, pushInstagramShareOpen },
  } = useAnalytics();

  const publishAnalyticsEvent = (metod: ShareMethod) => {
    sharedEvent(project, metod);
  };

  const handleInstagramClick = () => {
    handleInstagramShare();
    pushInstagramShareOpen();
  };

  const baseUrlForSharing = useMemo(() => {
    return getBaseUrlForSharing(project.slug);
  }, [project?.slug]);

  const shareLink = useMemo(() => {
    return getShareLink(baseUrlForSharing, {
      // TODO
      // utmContent: 'anonymous-...',d
      utmCampaign: 'share_button_ty',
      utmContent: currentUser?.id ?? `anonymous-${Math.random().toString(36).substring(7)}`,
    });
  }, [baseUrlForSharing, currentUser?.id]);

  const facebookShareLink = useMemo(() => {
    return getFacebookShareLink(baseUrlForSharing);
  }, [baseUrlForSharing]);

  const xShareLink = useMemo(() => {
    return getXShareLink(baseUrlForSharing);
  }, [baseUrlForSharing]);

  const whatsappShareLink = useMemo(() => {
    return getWhatsappShareLink(baseUrlForSharing);
  }, [baseUrlForSharing]);

  const handleNativeShareClick = useCallback(() => {
    if (!isNavigatorDefined) {
      return;
    }

    publishAnalyticsEvent(ShareMethod.GENERAL);

    navigator?.share?.({
      url: baseUrlForSharing,
      title: project.content.currentName,
      text: project.content.texts.perex,
    });
  }, [baseUrlForSharing, typeofNavigator, project.content.currentName, project.content.texts.perex]);

  const isNativeShareButtonVisible = useMemo(() => isNavigatorDefined && navigator?.canShare?.(), [typeofNavigator]);

  return (
    <>
      {/* Desktop icons */}
      <div className="hidden lg:flex flex-col gap-[8px] mb-[24px]">
        <ShareButton
          icon={<FacebookIcon />}
          tagSpecificProps={{
            htmlTag: ButtonHTMLTag.LINK,
            href: facebookShareLink,
            target: '_blank',
            onClick: () => publishAnalyticsEvent(ShareMethod.FACEBOOK),
          }}
          title={t('components.ThankYouPageShareStep.shareOnFacebook')}
          style={ButtonStyle.FACEBOOK}
        />
        <ShareButton
          tagSpecificProps={{
            htmlTag: ButtonHTMLTag.LINK,
            href: whatsappShareLink,
            target: '_blank',
            onClick: () => publishAnalyticsEvent(ShareMethod.WHATSAPP),
          }}
          title={t('components.ThankYouPageShareStep.shareOnWhatsapp')}
          icon={<WhatsappIcon />}
          style={ButtonStyle.WHATSAPP}
        />
        <ShareButton
          tagSpecificProps={{
            htmlTag: ButtonHTMLTag.LINK,
            href: xShareLink,
            target: '_blank',
            onClick: () => publishAnalyticsEvent(ShareMethod.X),
          }}
          title={t('components.ThankYouPageShareStep.shareOnX')}
          icon={<XIcon />}
          style={ButtonStyle.X}
        />
        <ShareButton
          tagSpecificProps={{ onClick: handlePrintPoster, htmlTag: ButtonHTMLTag.BUTTON }}
          title={t('components.ThankYouPageShareStep.printPoster')}
          icon={<PrintIcon />}
          size={ButtonSize.MEDIUM}
          style={ButtonStyle.PRIMARY_OUTLINED}
        />
      </div>

      {/* Mobile Icons */}
      <div className="lg:hidden flex flex-col gap-[8px] mb-[24px]">
        <ShareButton
          icon={<FacebookIcon />}
          tagSpecificProps={{
            target: '_blank',
            htmlTag: ButtonHTMLTag.LINK,
            href: facebookShareLink,
            onClick: () => publishAnalyticsEvent(ShareMethod.FACEBOOK),
          }}
          title={t('components.ThankYouPageShareStep.shareOnFacebook')}
          style={ButtonStyle.FACEBOOK}
        />

        {isNativeShareDefined && (
          <ShareButton
            icon={<InstagramIcon />}
            tagSpecificProps={{ htmlTag: ButtonHTMLTag.BUTTON, onClick: handleInstagramClick }}
            title={t('components.ThankYouPageShareStep.shareOnInstagram')}
            style={ButtonStyle.INSTAGRAM}
          />
        )}

        <ShareButton
          tagSpecificProps={{
            htmlTag: ButtonHTMLTag.LINK,
            target: '_blank',
            href: whatsappShareLink,
            onClick: () => publishAnalyticsEvent(ShareMethod.WHATSAPP),
          }}
          title={t('components.ThankYouPageShareStep.shareOnWhatsapp')}
          icon={<WhatsappIcon />}
          style={ButtonStyle.WHATSAPP}
        />

        {shouldDisplayMobileOtherOptions ? (
          <>
            <ShareButton
              tagSpecificProps={{
                htmlTag: ButtonHTMLTag.LINK,
                href: xShareLink,
                target: '_blank',
                onClick: () => publishAnalyticsEvent(ShareMethod.X),
              }}
              title={t('components.ThankYouPageShareStep.shareOnX')}
              icon={<XIcon />}
              style={ButtonStyle.X}
            />
            <ShareButton
              tagSpecificProps={{ onClick: handlePrintPoster, htmlTag: ButtonHTMLTag.BUTTON }}
              title={t('components.ThankYouPageShareStep.printPoster')}
              icon={<PrintIcon />}
              size={ButtonSize.MEDIUM}
              style={ButtonStyle.PRIMARY_OUTLINED}
            />
            {isNativeShareButtonVisible && (
              <>
                <ShareButton
                  tagSpecificProps={{ onClick: handleNativeShareClick, htmlTag: ButtonHTMLTag.BUTTON }}
                  title={t('components.ThankYouPageShareStep.shareMenuButtonText')}
                  icon={<ShareIcon />}
                  size={ButtonSize.MEDIUM}
                  style={ButtonStyle.PRIMARY_OUTLINED}
                />
              </>
            )}
          </>
        ) : (
          <ShareButton
            tagSpecificProps={{
              onClick: () => setShouldDisplayMobileOtherOptions(true),
              htmlTag: ButtonHTMLTag.BUTTON,
            }}
            title={t('components.ThankYouPageShareStep.otherOptions')}
            icon={<ChevronIcon />}
            size={ButtonSize.MEDIUM}
            style={ButtonStyle.PRIMARY_OUTLINED}
          />
        )}
      </div>

      <div>
        <label className="block font-medium mb-[8px] text-center">
          {t('components.ThankYouPageShareStep.shareLink')}
        </label>
        <CopyInput onClick={() => publishAnalyticsEvent(ShareMethod.GENERAL)} value={shareLink} />
      </div>
    </>
  );
};
